import { balance as defaultBalance, type Balance } from '../balance';
import { STRENGTH } from '../matchup';
import { SLOT_POSITION } from '../playdef';
import { physicalTypeMult } from '../typechart';
import type { Combatant, DefenseSlot, OffenseSlot, TypeName } from '../types';

/**
 * The matchup board (slice 7): the seven battles across the line, paired as the engine pairs them
 * by default (play.ts DEFAULT_COVER and BLOCK_ORDER), and the win chance of each from the same
 * numbers a battle uses: effective stats (stages, status, fatigue, position fit) and type factors.
 * Every AI rule scores Pokémon here, so the AI weighs exactly what the player's board shows.
 */

/** The battles the AI weighs: the matchup kinds a play's roles fight, plus the QB's throw. */
export type BoardKind = 'block' | 'coverage' | 'tackle' | 'throw';

export interface BoardLine {
  offense: OffenseSlot;
  defense: DefenseSlot;
  kind: BoardKind;
}

export const BOARD: readonly BoardLine[] = [
  { offense: 'OL1', defense: 'DL1', kind: 'block' },
  { offense: 'OL2', defense: 'DL2', kind: 'block' },
  { offense: 'WR1', defense: 'CB1', kind: 'coverage' },
  { offense: 'WR2', defense: 'CB2', kind: 'coverage' },
  { offense: 'TE', defense: 'S', kind: 'coverage' },
  { offense: 'RB', defense: 'LB1', kind: 'tackle' },
  { offense: 'QB', defense: 'LB2', kind: 'throw' },
];

const byOffense = new Map(BOARD.map((l) => [l.offense as string, l]));
const byDefense = new Map(BOARD.map((l) => [l.defense as string, l]));

/** A slot's board line, from either side. */
export function lineOf(slot: OffenseSlot | DefenseSlot): BoardLine {
  const line = byOffense.get(slot) ?? byDefense.get(slot);
  if (!line) throw new Error(`no board line for ${slot}`);
  return line;
}

/** The slot across the board from `slot`. */
export function opponentOf(slot: OffenseSlot | DefenseSlot): OffenseSlot | DefenseSlot {
  const line = lineOf(slot);
  return line.offense === slot ? line.defense : line.offense;
}

/** `c` as it would play `slot`: its position set (position fit reads it), stages kept or cleared. */
export function atSlot(
  c: Combatant,
  slot: OffenseSlot | DefenseSlot,
  clearStages = false,
): Combatant {
  const position = SLOT_POSITION[slot];
  if (c.position === position && !clearStages) return c;
  return { ...c, position, ...(clearStages ? { stages: {} } : {}) };
}

/**
 * The chance that a side of strength `ours` beats one of strength `theirs` in one battle: each
 * side's roll is uniform in rolls.min..rolls.max (option A), and a crit (crits.rate) goes to
 * either side at even odds. Exact: P(ratio x X > Y) for X, Y ~ U(lo, hi).
 */
export function winChance(ours: number, theirs: number, b: Balance): number {
  const { min: lo, max: hi } = b.rolls;
  const w = hi - lo;
  const r = ours / Math.max(1e-9, theirs);
  const a = Math.min(hi, Math.max(lo, lo / r));
  const c = Math.min(hi, Math.max(lo, hi / r));
  const integral = (r * (c * c - a * a)) / 2 - lo * (c - a) + w * (hi - c);
  const p = w > 0 ? integral / (w * w) : r >= 1 ? 1 : 0;
  return (1 - b.crits.rate) * p + b.crits.rate * 0.5;
}

/** The best win chance a battle can give: a sure win but for the crit check. */
export function maxWinChance(b: Balance): number {
  return 1 - b.crits.rate * 0.5;
}

/**
 * Each side's strength in a battle of `kind` with no spent move, before the roll: the matchup's
 * stats (matchup.ts STRENGTH) times its type factor; the throw is positional (no type).
 */
export function strengths(
  kind: BoardKind,
  offense: Combatant,
  defense: Combatant,
  b: Balance = defaultBalance,
): { offense: number; defense: number } {
  const s = STRENGTH[kind];
  const a = s.a(offense, b);
  const d = s.b(defense, b);
  if (kind === 'throw') return { offense: a, defense: d };
  const { physicalFloor: floor, battleExponent: exponent } = b.types;
  return {
    offense: a * typeMult(offense.types, defense.types, floor, exponent),
    defense: d * typeMult(defense.types, offense.types, floor, exponent),
  };
}

/**
 * physicalTypeMult, remembered by the two type lists. A Pokémon's `types` array is shared by every
 * copy of it (spreads keep the reference), so the board works each pairing out once. Only the
 * default floor and exponent are remembered: any other balance (a harness sweep) works it out.
 */
const TYPE_MULTS = new WeakMap<readonly TypeName[], WeakMap<readonly TypeName[], number>>();
function typeMult(
  mine: readonly TypeName[],
  theirs: readonly TypeName[],
  floor: number,
  exponent: number,
): number {
  if (
    floor !== defaultBalance.types.physicalFloor ||
    exponent !== defaultBalance.types.battleExponent
  ) {
    return physicalTypeMult(mine, theirs, floor, exponent);
  }
  let row = TYPE_MULTS.get(mine);
  if (!row) {
    row = new WeakMap();
    TYPE_MULTS.set(mine, row);
  }
  let m = row.get(theirs);
  if (m === undefined) {
    m = physicalTypeMult(mine, theirs, floor, exponent);
    row.set(theirs, m);
  }
  return m;
}

/** The offense's win chance in a battle of `kind` between these two, as they stand. */
export function offenseWinChance(
  kind: BoardKind,
  offense: Combatant,
  defense: Combatant,
  b: Balance = defaultBalance,
): number {
  const s = strengths(kind, offense, defense, b);
  return winChance(s.offense, s.defense, b);
}

/** A win chance for whichever side `ours` is on. */
export function sideWinChance(
  side: 'offense' | 'defense',
  kind: BoardKind,
  ours: Combatant,
  theirs: Combatant,
  b: Balance = defaultBalance,
): number {
  return side === 'offense'
    ? offenseWinChance(kind, ours, theirs, b)
    : 1 - offenseWinChance(kind, theirs, ours, b);
}
